import {useAppState} from "@/store";
import Panel from "@/components/Panel";
import Input from "@/components/fields/Input";
import NextButton from "@/components//NextButton";
import FaceIcon from "@/components/icons/FaceIcon";
import StepTitles from "@/components/StepTitles";
import DOMPurify from "dompurify";
import {useValidation} from "@/validation";
import StepFooter from "@/components/StepFooter";
import {apiUrl, dataLayerPush} from "@/helpers";
import {API_ENDPOINTS, REFERRAL_OPTIONS} from "@/constants";
import {useApi} from "@/api";
import {toast} from "react-toastify";
import {useEffect} from "react";
import Select from "@/components/fields/Select";
import Toggle from "@/components/fields/Toggle";
import ChoiceMulti from "@/components/fields/ChoiceMulti";

export default function ContactDetails({goToStep}) {

    const {state, setState} = useAppState()
    const {api, setLoading} = useApi()
    const {preSubmitValidate} = useValidation()

    const handleSubmit = async () => {
        setLoading(true)

        try {
            preSubmitValidate()

            const response = await api.post(apiUrl(API_ENDPOINTS.contact), {
                "requestId": state.requestId,
                "firstName": state.contact.first_name,
                "lastName": state.contact.last_name,
                "email": state.contact.email,
                "phone": state.contact.phone,
                "referredBy": state.contact.referred_by,
                "readiness": state.contact.readiness
            })

            setState((s) => {
                s.customerId = response.customerId
                s.availabilities = response.availabilities
                s.estimate = response.estimate
            })

            const availability = response.availabilities?.find(a => a.date === state.preferred_date.raw)

            if (availability) {
                setState((s) => {
                    s.chosen_date = s.preferred_date
                    s.chosen_rate = Math.min(availability.price, availability.salePrice)
                    s.chosen_time = null
                    s.availability = availability
                })
            } else {
                setState((s) => {
                    s.chosen_date = null
                    s.chosen_rate = null
                    s.chosen_time = null
                    s.availability = null
                })
            }

            await dataLayerPush('book-contact-info-submit', {
                ['book-contact-email']: state.contact.email,
                ['book-contact-first-name']: state.contact.first_name,
                ['book-contact-last-name']: state.contact.last_name,
                ['book-contact-phone']: state.contact.phone,
                ['book-assigned-branch']: state.branch?.name || null,
                ['book-estimate-id']: response.customerId || null,
                ['book-contact-readiness']: state.contact.readiness,
            })

            goToStep(5)
        } catch (e) {
            toast.error(e.message)
        }

        setLoading(false)
    }

    const handlePhoneChange = (value) => {

        const format_phone = (number) => {
            number = number.replace('+1', '').trim().replace(/[^0-9]/g, "");

            if (number.length < 4) return number;
            if (number.length < 7) return number.replace(/(\d{3})(\d{1})/, "$1-$2");
            if (number.length < 11) return number.replace(/(\d{3})(\d{3})(\d{1})/, "+1 ($1) $2-$3");
            return number.replace(/(\d{3})(\d{4})(\d{4})/, "+1 ($1) $2-$3");
        }

        setState((s) => {s.contact.phone = format_phone(value)})
    }

    useEffect(() => {
        dataLayerPush('book-contact-info-view', {
            ['book-assigned-branch']: state.branch?.name || null
        }).then()
    }, [])

    const content = window.ein?.content?.step_4 || {}

    const {title, secondary_title, tertiary_title, disclaimer, button_label, readiness} = content

    return (
        <div className={"grid grid-cols-4 lg:grid-cols-12 gap-x-[20px] lg:gap-x-[32px] gap-y-[40px]"}>
            <div className="col-span-4 lg:col-span-12 flex flex-col items-center text-center">
                <FaceIcon className={'size-[80px] mb-[16px]'}/>
                <StepTitles title={title} secondaryTitle={secondary_title} tertiaryTitle={tertiary_title}/>
            </div>
            <div className="col-span-4 lg:col-span-8 lg:col-start-3">
                <Panel>
                    <div className={"grid grid-cols-1 gap-[24px] lg:gap-[32px]"}>
                        <Input
                            label={"First Name"}
                            type={"text"}
                            name={"contact.first_name"}
                            value={state.contact?.first_name || ''}
                            onChange={(v) => setState((s) => {s.contact.first_name = v})}
                            required
                        />
                        <Input
                            label={"Last Name"}
                            type={"text"}
                            name={"contact.last_name"}
                            value={state.contact?.last_name || ''}
                            onChange={(v) => setState((s) => {s.contact.last_name = v})}
                            required
                        />
                        <Input
                            label={"Email Address"}
                            type={"email"}
                            name={"contact.email"}
                            value={state.contact?.email || ''}
                            onChange={(v) => setState((s) => {s.contact.email = v})}
                            required
                        />
                        <Input
                            label={"Phone Number"}
                            type={"tel"}
                            name={"contact.phone"}
                            value={state.contact?.phone || ''}
                            onChange={handlePhoneChange}
                            maxLength={17}
                            required
                        />
                        <Select
                            label={"How did you hear about us?"}
                            name={`contact.referred_by`}
                            options={REFERRAL_OPTIONS.map(opt => ({value: opt, label: opt}))}
                            value={state.contact?.referred_by}
                            placeholder={"-- Select --"}
                            onChange={(v) => setState((s) => {s.contact.referred_by = v})}
                            required
                        />
                        <Toggle
                            label={<span>I accept the <a href="/terms-conditions" target={'_blank'}>terms and conditions</a> and <a href="/privacy-policy" target={"_blank"}>privacy policy</a>.</span>}
                            name={`contact.accept_terms`}
                            checked={state.contact.accept_terms || ''}
                            onChange={checked => setState((s) => {s.contact.accept_terms = checked})}
                        />
                    </div>
                </Panel>
            </div>
            <div className="col-span-4 lg:col-span-8 lg:col-start-3">
                <Panel feature={true} passive={true}>
                    <div className="smart-tag mb-[24px] lg:mb-[32px]">
                        🧠 Smart Move System™
                    </div>
                    {disclaimer.title && (
                        <div className="heading-h3 mb-[8px] lg:mb-[16px]">
                            {disclaimer.title}
                        </div>
                    )}
                    {disclaimer.text && (
                        <div
                            dangerouslySetInnerHTML={{'__html': DOMPurify.sanitize(disclaimer.text)}}
                        />
                    )}
                    <div className={"flex flex-col lg:flex-row gap-[16px] lg:gap-[32px]"}>
                        {disclaimer.left_list && (
                            <ul className={"lg:w-1/2"}>
                                {disclaimer.left_list.map((item, i) => (
                                    <li key={i}>{item.row}</li>
                                ))}
                            </ul>
                        )}
                        {disclaimer.right_list && (
                            <ul className={"lg:w-1/2"}>
                                {disclaimer.right_list.map((item, i) => (
                                    <li key={i}>{item.row}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Panel>
            </div>
            <div className="col-span-4 lg:col-span-8 lg:col-start-3">
                <Panel passive={true} className="ein-panel-blue">
                    {readiness?.question &&
                        <div className="heading-h4 text-center"
                             dangerouslySetInnerHTML={{'__html': DOMPurify.sanitize(readiness.question)}}
                        />
                    }
                    {readiness?.hint &&
                        <div className={"text-center italic text-brand-grey text-sm mt-[4px]"}
                             dangerouslySetInnerHTML={{'__html': DOMPurify.sanitize(readiness.hint)}}
                        />
                    }
                    {readiness?.option_ready && readiness?.option_comparing && readiness?.option_ballpark &&
                        <ChoiceMulti
                            stateKey={"contact"}
                            name={"readiness"}
                            labels={[
                                <>{DOMPurify.sanitize(readiness.option_ready)}</>,
                                <>{DOMPurify.sanitize(readiness.option_comparing)}</>,
                                <>{DOMPurify.sanitize(readiness.option_ballpark)}</>
                            ]}
                            values={[
                                "ready",
                                "comparing",
                                "ballpark"
                            ]}
                        />
                    }
                </Panel>
            </div>
            <div className="col-span-4 lg:col-span-12 flex justify-center">
                <NextButton onClick={handleSubmit} text={button_label} disabled={false} />
            </div>
            <div className="col-span-4 lg:col-span-12">
                <StepFooter/>
            </div>
        </div>
    )
}
